import { useEffect, useMemo, useRef, useState } from "react";
import { BehaviorSubject, timer, from, concatMap, switchMap } from "rxjs";
import type { Subscription } from "rxjs";
import { z } from "zod";
import { Html5Qrcode } from "html5-qrcode";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createClient, createTrpcDirectLink, type WebSocketBusClient } from "@/lib/exchange";
import { createTRPCProxyClient } from "@trpc/client";
import type { RemoteUploadRouter } from "@/lib/remote-upload.d";
import "./index.css";

type OnlineStatus = "unknown" | "online" | "offline";

// 默认 WebSocket URL（可通过环境变量覆盖）
const DEFAULT_WS_URL = "wss://lensflow-exchange.hemaoptical.com";

// 获取认证 token 的 API 端点
const AUTH_API_URL = "https://lensflow-exchange.hemaoptical.com/auth/token";

/**
 * 获取认证 token
 */
async function getAuthToken(): Promise<{ token: string; clientId: string }> {
  try {
    const res = await fetch(AUTH_API_URL, { method: "POST" });
    if (!res.ok) {
      throw new Error(`Failed to get token: ${res.status}`);
    }
    const data = await res.json();
    return {
      token: data.token,
      clientId: data.client_id,
    };
  } catch (error) {
    console.error("获取 token 失败:", error);
    throw error;
  }
}

function useTrpcClient() {
  const wsClientRef = useRef<WebSocketBusClient | null>(null);
  const trpcClientRef = useRef<ReturnType<typeof createTRPCProxyClient<RemoteUploadRouter>> | null>(null);
  const [peerId, setPeerId] = useState<string | null>(null);
  const [selfClientId, setSelfClientId] = useState<string | null>(null);

  useEffect(() => {
    let disposed = false;

    (async () => {
      try {
        // 获取 token
        const { token, clientId } = await getAuthToken();
        if (disposed) return;

        setSelfClientId(clientId);

        // 创建 WebSocket 客户端
        const wsClient = createClient({ url: DEFAULT_WS_URL, token });
        await wsClient.connect();
        
        if (disposed) {
          wsClient.disconnect();
          return;
        }

        wsClientRef.current = wsClient;
      } catch (error) {
        console.error("WebSocket 连接失败:", error);
      }
    })();

    return () => {
      disposed = true;
      if (wsClientRef.current) {
        try {
          wsClientRef.current.disconnect();
        } catch {}
        wsClientRef.current = null;
      }
    };
  }, []);

  // 当 peerId 改变时，重新创建 tRPC 客户端
  useEffect(() => {
    if (!peerId || !wsClientRef.current) {
      trpcClientRef.current = null;
      return;
    }

    trpcClientRef.current = createTRPCProxyClient<RemoteUploadRouter>({
      links: [
        createTrpcDirectLink<RemoteUploadRouter>({
          ws: wsClientRef.current,
          peerId,
          timeoutMs: 15_000,
        }),
      ],
    });
  }, [peerId]);

  return { trpcClientRef, wsClientRef, peerId, setPeerId, selfClientId } as const;
}

function inferExt(file: File): string {
  const byName = (file.name?.split(".").pop() || "").toLowerCase();
  if (byName && byName !== file.name) return byName;
  const byType = (file.type?.split("/").pop() || "").toLowerCase();
  return byType || "";
}

function extractUuidV7(text: string): string | null {
  const direct = z.uuidv7().safeParse(text);
  if (direct.success) return text;
  try {
    const u = new URL(text);
    const candidate = u.searchParams.get("text");
    if (candidate && z.uuidv7().safeParse(candidate).success) return candidate;
  } catch {}
  const m = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-7[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}/.exec(text);
  if (m && z.uuidv7().safeParse(m[0]).success) return m[0];
  return null;
}

export function App() {
  const { trpcClientRef, peerId, setPeerId, selfClientId } = useTrpcClient();

  const [online, setOnline] = useState<OnlineStatus>("unknown");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const qrRef = useRef<Html5Qrcode | null>(null);
  const qrDivId = "qr-reader";

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [currentFilename, setCurrentFilename] = useState<string | null>(null);
  const abortRef = useRef<(() => void) | null>(null);

  const inputCameraRef = useRef<HTMLInputElement | null>(null);
  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const [manualOpen, setManualOpen] = useState(false);
  const [manualIdInput, setManualIdInput] = useState("");
  const [manualIdError, setManualIdError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      const q = qrRef.current;
      qrRef.current = null;
      if (!q) return;
      (async () => {
        try { await (q as any).stop?.(); } catch {}
        try { await (q as any).clear?.(); } catch {}
      })();
    };
  }, []);

  async function startScan() {
    if (scanning) return;
    setScanError(null);
    try {
      // show overlay first, then wait for it to render before initializing scanner
      setScanning(true);
      // ensure DOM updates (overlay + target div rendered)
      await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
      const mountEl = document.getElementById(qrDivId);
      if (!mountEl) {
        // in rare cases, wait one more frame
        await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
      }
      const q = new Html5Qrcode(qrDivId);
      qrRef.current = q;
      await q.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: 240,
        } as any,
        async (decodedText) => {
          const id = extractUuidV7(decodedText);
          if (!id) {
            setScanError("无效二维码：未检测到 UUIDv7");
            return;
          }
          setPeerId(id);
          await stopScan();
        },
        () => {}
      );
    } catch (err) {
      console.error(err);
      setScanning(false);
      setScanError(err instanceof Error ? err.message : "开启摄像头失败");
    }
  }

  async function stopScan() {
    if (!qrRef.current) {
      setScanning(false);
      return;
    }
    try {
      await qrRef.current.stop();
    } catch {}
    try {
      await qrRef.current.clear();
    } catch {}
    qrRef.current = null;
    setScanning(false);
  }

  function openManual() {
    setManualIdInput(peerId || "");
    setManualIdError(null);
    setManualOpen(true);
  }

  async function confirmManual() {
    const id = extractUuidV7(manualIdInput.trim());
    if (!id) {
      setManualIdError("请输入有效的 UUIDv7");
      return;
    }
    setPeerId(id);
    if (scanning) await stopScan();
    setManualOpen(false);
  }

  async function pingOnce(timeoutMs: number = 2000): Promise<boolean> {
    const client = trpcClientRef.current;
    if (!client) return false;
    try {
      const result = await client.ping.query({ message: "hello" });
      return result.pong === true;
    } catch (error) {
      console.error("Ping 失败:", error);
      return false;
    }
  }

  const pingSubRef = useRef<Subscription | null>(null);
  useEffect(() => {
    if (pingSubRef.current) {
      try { pingSubRef.current.unsubscribe(); } catch {}
      pingSubRef.current = null;
    }

    if (!peerId || !trpcClientRef.current) {
      setOnline("unknown");
      return;
    }

    let disposed = false;
    type Mode = "normal" | "degraded" | "offline";
    const periods: Record<Mode, number> = { normal: 10_000, degraded: 5_000, offline: 3_000 };
    let mode: Mode = "normal";
    let failStreak = 0;
    const mode$ = new BehaviorSubject<Mode>(mode);

    (async () => {
      const ok = await pingOnce(2000);
      if (disposed) return;
      if (ok) {
        setOnline("online");
        failStreak = 0;
        mode = "normal";
        mode$.next("normal");
      } else {
        failStreak = 1;
        mode = "degraded";
        mode$.next("degraded");
      }
    })();

    const ticks$ = mode$.pipe(
      switchMap((m) => timer(periods[m], periods[m]).pipe(concatMap(() => from(pingOnce(2000)).pipe()))),
    );

    const sub = ticks$.subscribe((ok) => {
      if (ok) {
        setOnline("online");
        failStreak = 0;
        if (mode !== "normal") {
          mode = "normal";
          mode$.next("normal");
        }
        return;
      }

      if (mode === "normal") {
        failStreak = 1;
        mode = "degraded";
        mode$.next("degraded");
        return;
      }
      if (mode === "degraded") {
        failStreak = 2;
        setOnline("offline");
        mode = "offline";
        mode$.next("offline");
        return;
      }
      // offline mode keeps probing; remain offline until success
      setOnline("offline");
    });

    pingSubRef.current = sub;

    return () => {
      disposed = true;
      try { sub.unsubscribe(); } catch {}
      try { mode$.complete(); } catch {}
    };
  }, [peerId, trpcClientRef.current]);

  async function handleUpload(file: File) {
    if (!peerId) {
      setUploadError("请先扫码或手动输入对端 ID");
      return;
    }
    
    const client = trpcClientRef.current;
    if (!client) {
      setUploadError("tRPC 客户端未就绪");
      return;
    }

    setErrorMsg(null);
    setUploadError(null);
    setUploading(true);
    setUploadProgress(0);
    setCurrentFilename(file.name || "文件");
    abortRef.current = null;

    try {
      const reachable = await pingOnce();
      if (!reachable) throw new Error("对端不在线或不可达");

      const ext = inferExt(file);
      const presign = await client.presign.mutate({ ext });

      const contentType = presign.content_type || file.type || "application/octet-stream";

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        abortRef.current = () => xhr.abort();
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(pct);
          }
        };
        xhr.onerror = () => reject(new Error("网络错误"));
        xhr.onabort = () => reject(new Error("已取消"));
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`上传失败 ${xhr.status}`));
        };
        xhr.open("PUT", presign.upload_url);
        xhr.setRequestHeader("Content-Type", contentType);
        xhr.send(file);
      });

      await client.insertImage.mutate({
        url: presign.public_url,
        content_type: contentType,
        filename: file.name,
      });

      setUploadProgress(100);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "上传失败";
      setUploadError(msg);
    } finally {
      setUploading(false);
      abortRef.current = null;
    }
  }

  async function onPickFromInput(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // reset so selecting the same files again still triggers change
    e.target.value = "";
    for (const file of files) {
      await handleUpload(file);
    }
  }

  function StatusBadge() {
    const color = online === "online" ? "bg-green-500" : online === "offline" ? "bg-red-500" : "bg-gray-400";
    const text = online === "online" ? "在线" : online === "offline" ? "离线" : "未知";
    return (
      <div className="flex items-center gap-2">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`}></span>
        <span className="text-sm text-muted-foreground">{text}</span>
      </div>
    );
  }

  const actionDisabled = scanning || uploading || online !== "online" || !peerId;

  return (
    <div className="mx-auto w-full p-3 sm:p-6 max-w-2xl relative z-10">
      <Card>
        <CardHeader className="gap-2">
          <CardTitle className="text-2xl font-bold">手机扫码上传</CardTitle>
          <CardDescription>扫码获取对端 UUID，上传图片或文件</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <StatusBadge />
            <div className="text-xs text-muted-foreground break-all">
              {peerId ? `Peer: ${peerId}` : "未绑定对端"}
            </div>
          </div>

          {selfClientId && (
            <div className="text-xs text-muted-foreground">
              本机 ID: {selfClientId}
            </div>
          )}

          <div className="flex gap-3">
            <Button onClick={() => void startScan()} variant="default">扫码获取 ID</Button>
            <Button onClick={() => openManual()} variant="outline">手动输入 ID</Button>
            {peerId && (
              <Button onClick={() => void startScan()} variant="secondary">重新扫码</Button>
            )}
          </div>

          {/* scanning overlay */}
          {scanning && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
              <div className="bg-background rounded-lg shadow-lg w-full max-w-sm p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">扫码获取对端 ID</div>
                  <Button variant="ghost" size="sm" onClick={() => void stopScan()}>
                    关闭
                  </Button>
                </div>
                <div className="rounded-md overflow-hidden">
                  <div id={qrDivId} />
                </div>
                <div className="text-xs text-muted-foreground">请将二维码置于取景框内自动识别</div>
              </div>
            </div>
          )}

          {scanError && <div className="text-sm text-red-500">{scanError}</div>}
          {errorMsg && <div className="text-sm text-red-500">{errorMsg}</div>}

          <div className="space-y-2">
            <div className="flex gap-2">
              <Button disabled={actionDisabled} onClick={() => inputCameraRef.current?.click()}>拍照</Button>
              <Button disabled={actionDisabled} onClick={() => inputFileRef.current?.click()}>选取文件</Button>
            </div>
            <div className="text-xs text-muted-foreground">
              {online !== "online" ? "对端离线或未连接，无法上传" : "可选择拍照、相册或文件进行上传"}
            </div>
          </div>

          <input ref={inputCameraRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={onPickFromInput} />
          <input ref={inputFileRef} type="file" accept="*/*" multiple className="hidden" onChange={onPickFromInput} />

          {uploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <div className="truncate mr-2">{currentFilename}</div>
                <div>{uploadProgress}%</div>
              </div>
              <div className="h-2 rounded bg-muted overflow-hidden">
                <div className="h-full bg-primary transition-[width]" style={{ width: `${uploadProgress}%` }}></div>
              </div>
              <div className="flex justify-end">
                <Button variant="destructive" onClick={() => abortRef.current?.()} disabled={!uploading}>取消上传</Button>
              </div>
            </div>
          )}

          {uploadError && <div className="text-sm text-red-500">{uploadError}</div>}

          {manualOpen && (
            <div className="fixed inset-0 z-20 bg-black/40 flex items-center justify-center p-4">
              <div className="w-full max-w-md rounded-md border bg-card p-4 shadow-lg">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="manual-uuid">输入 UUID 或包含 UUID 的文本</Label>
                    <Input
                      id="manual-uuid"
                      placeholder="例如：018f… 或含有 UUID 的链接"
                      value={manualIdInput}
                      onChange={(e) => setManualIdInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void confirmManual();
                      }}
                      autoFocus
                    />
                    {manualIdError && <div className="text-xs text-red-500">{manualIdError}</div>}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={() => setManualOpen(false)}>取消</Button>
                    <Button onClick={() => void confirmManual()}>确认</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default App;
